import React from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Link, useNavigate, useParams } from "react-router-dom"; // Import useNavigate from react-router-dom
import { useEffect, useState } from "react";
import {
  getDatabase,
  ref,
  onValue,
  get,
  set,
  push,
  update,
  serverTimestamp,
} from "firebase/database";
import {
  getBookingByID,
  sendEmail,
  updateBookingStatus,
  checkCoupon,
  getInquiryDataById,
  getPlatformPercentages,
  getCurrentUserDetails,
  getUserByID,
  BookingStatusUpdateEmailBody,
  getOwnerMeta,
  getUserIdByRefferralCode,
  getListingByID,
  getCommissionRateByReferralCode,
} from "./utils";
import dayjs from "dayjs";
import { useAuth } from "../contexts/AuthContext";
import { useFormContext } from "../contexts/FormContext";
import TextField from "@mui/material/TextField";
import { Alert, Checkbox, FormControlLabel } from "@mui/material";
import { Col, Row, Button, Modal } from "react-bootstrap";
import { ThreeDots } from "react-loader-spinner";

// Helper function to determine if a rental is taxable
// Tax (8.25%) is ONLY collected for rentals in Texas that are driven by the renter (no captain).
// No tax for: Texas charters/captain-driven rentals, or ANY rental outside of Texas.
const checkIfRentalIsTaxable = async (listingData) => {
  try {
    // boatCaptain === true means only owner/captain drives (charter) => NOT taxable
    // boatCaptain === false means renter can drive => potentially taxable (if in Texas)
    if (listingData?.boatCaptain === true) {
      console.log("Tax check: Captain/charter rental - not taxable");
      return false;
    }

    // Check if listing is in Texas using reverse geocoding
    const lat = listingData?.storageAddress?.lat;
    const lng = listingData?.storageAddress?.lng;
    if (!lat || !lng) {
      console.log("Tax check: No location data - not taxable");
      return false;
    }

    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=AIzaSyBfEcoyW9DM7QQWdV3oTjevrfyhX5n5qqg`
    );
    const data = await response.json();
    const addressComponents = data.results[0]?.address_components || [];
    const stateComponent = addressComponents.find((component) =>
      component.types.includes("administrative_area_level_1")
    );
    const state = stateComponent?.short_name || "";
    console.log("Tax check: State =", state, "| boatCaptain =", listingData?.boatCaptain);

    // Only taxable if in Texas (TX) and renter-driven (boatCaptain is false)
    return state === "TX";
  } catch (error) {
    console.error("Error checking tax eligibility:", error);
    return false; // Default to not taxable on error
  }
};

const PaymentFormTest = ({ amount }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate(); // Initialize useNavigate
  const auth = useAuth();
  const { setIsFormDirty, setFormType, setFormMessage } = useFormContext();

  const [storedBookingId, setBookingId] = useState("");
  const [storedPaymentId, setPaymentId] = useState("");
  const [bookingData, setBookingData] = useState("");
  const [acknowledgement2, setAcknowledgement2] = useState(false);
  const [offer, setOfferData] = useState("");
  const [inquiryData, setInquiryData] = useState([]);

  const [totalTax, setTotalTax] = useState(0);
  const [adminTax, setAdminTax] = useState(0);
  const [amountTotal, setAmountTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [coupon, setCouponCode] = useState("");
  const [isTaxable, setIsTaxable] = useState(false);

  const [discount, setDiscount] = useState(0);
  const [totalOwnerBookings, setTotalOwnerBookings] = useState(0);

  const queryParameters = new URLSearchParams(window.location.search);
  const bookingId = queryParameters.get("bookingId");
  const [platformPercentages, setPlatformPercentages] = useState({});

  const handleAcknowledgement = (acknowledgementName) => {
    if (acknowledgementName === "acknowledgement1") {
    } else if (acknowledgementName === "acknowledgement2") {
      setAcknowledgement2((prev) => !prev);
    }
  };

  const [listingData, setListingData] = useState([]);
  const [shouldBlockNavigation, setShouldBlockNavigation] = useState(true);
  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Set form as dirty to show navigation warning when user tries to leave payment page
  useEffect(() => {
    setIsFormDirty(true);
    setFormType('payment');
    setFormMessage('You have a pending payment. You can complete this payment within 24 hours, otherwise the booking will be cancelled. Do you want to leave this page?');

    // Add beforeunload listener to warn on browser close/refresh
    const handleBeforeUnload = (e) => {
      if (shouldBlockNavigation) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      // Clear form dirty state when leaving the payment page
      setIsFormDirty(false);
      setFormType('');
      setFormMessage('');
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [setIsFormDirty, setFormType, setFormMessage, shouldBlockNavigation]);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const bookingDetails = await getBookingByID(bookingId);
        if (!bookingDetails) {
          // No need to proceed if bookingDetails is null
          return;
        }

        const listingDetails = await getListingByID(bookingDetails.listingId);
        setListingData(listingDetails);

        const inquiryDetails = await getInquiryDataById(
          bookingDetails["inquiryId"]
        );
        const ownerMetaDetails = await getOwnerMeta(bookingDetails.owner);

        if (ownerMetaDetails?.totalPaidBookings != null) {
          setTotalOwnerBookings(ownerMetaDetails.totalPaidBookings); // Corrected function name
        } else {
          setTotalOwnerBookings(0);
        }

        // Logging the updated value of totalOwnerBookings here
        console.log(
          "totalOwnerBookings:",
          ownerMetaDetails?.totalPaidBookings ?? 0
        );

        setInquiryData(inquiryDetails);
        const offerDetails = inquiryDetails["offerData"];

        const platformFeesData = await getPlatformPercentages();

        // Check if this rental is taxable (only Texas rentals driven by renter)
        const taxable = await checkIfRentalIsTaxable(listingDetails);
        setIsTaxable(taxable);

        let platformData = {
          salesTax: taxable ? parseFloat(platformFeesData.defaultSalesTax) : 0,
        };

        if (listingDetails.boatType === "Bass Boat") {
          platformData.adminCut = parseFloat(platformFeesData.defaultAdminCut);
          console.log("bass boat");
        } else {
          platformData.adminCut = parseFloat(
            platformFeesData.defaultOtherBoatTypeAdminCut
          );
          console.log("other type");
        }

        console.log("Tax eligibility:", taxable ? "TAXABLE (Texas, renter-driven)" : "NOT TAXABLE");
        setPlatformPercentages(platformData);
        setOfferData(offerDetails);
        setBookingId(bookingId);
        setBookingData(bookingDetails);
        setPaymentId(bookingDetails.paymentId);
      } catch (error) {
        console.error("Error fetching listing details:", error);
      }
    };

    fetchDetails();
  }, [bookingId]); // Ensure to include bookingId in the dependency array
  // Empty dependency array means the effect runs once when the component mounts

  const verifyDiscount = async (code) => {
    const coupon = await checkCoupon(code);
    if (coupon != null) {
      setDiscount(parseInt(coupon[0].discount * 100));
    }
  };

  const [error, setError] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    if (!acknowledgement2) {
      setError("You must acknowledge to continue.");
      setLoading(false);
    }

    if (!stripe || !elements) {
      return;
    }

    const { token, error } = await stripe.createToken(
      elements.getElement(CardElement)
    );

    if (error) {
      console.error(error);
      setError(
        error.message ||
          "There was an error processing your card. Please try again."
      );
      setLoading(false); // Don't forget to set loading to false here as well
      return;
    } else {
      const taxTotal = platformPercentages.salesTax * amount;
      setTotalTax(taxTotal);

      const taxAdmin = platformPercentages.adminCut * taxTotal;
      setAdminTax(taxAdmin);

      let cutAdmin = 0;
      let refferalAmount = 0;
      let adminFirstTimeCut = 0;
      const ownerData = await getUserByID(bookingData.owner);
      const referId = await getUserIdByRefferralCode(ownerData.refferalCode);

      // Get the commission rate based on owner's referral code
      // Special codes like "START@5" have custom rates (e.g., 5% instead of 10%)
      const ownerCommissionRate = await getCommissionRateByReferralCode(ownerData.refferalCode);
      console.log(`Using commission rate: ${ownerCommissionRate * 100}% for referral code: ${ownerData.refferalCode}`);

      if (totalOwnerBookings > 0) {
        cutAdmin = ownerCommissionRate * amount;
      } else if (totalOwnerBookings === 0) {
        if (referId) {
          const database = getDatabase();
          cutAdmin = ownerCommissionRate * amount + 4000; // 4000 = $40
          adminFirstTimeCut = 4000;
          const referPayments = ref(database, `ownerMeta/${referId}`);

          const previousReferPaymentsSnapshot = await get(referPayments);

          if (previousReferPaymentsSnapshot.exists()) {
            const referPaymentData = previousReferPaymentsSnapshot.val();

            const previousCurrentAmount =
              referPaymentData.payments.currentAmount;
            const previousTotalAmount = referPaymentData.payments.totalAmount;
            const referPaymentObject = {
              currentAmount: previousCurrentAmount + 3000,
              totalAmount: previousTotalAmount + 3000,
            };
            await update(referPayments, { payments: referPaymentObject });
            refferalAmount = 3000;
          }
        } else {
          cutAdmin = ownerCommissionRate * amount + 10000; // 10000 = $100
          adminFirstTimeCut = 10000;
        }
      }

      const finalAmount =
        parseInt(amount + taxTotal) - (discount > 0 ? discount : 0);
      setAmountTotal(finalAmount);
      console.log(finalAmount);

      // Charter deposit percentage logic (in cents)
      const charterDepositPercentage = listingData?.fishingGuideDetails?.policies?.paymentTerms?.depositPercentage
        ? parseFloat(listingData.fishingGuideDetails.policies.paymentTerms.depositPercentage)
        : null;
      const isCharterDeposit = charterDepositPercentage !== null && charterDepositPercentage > 0 && charterDepositPercentage < 100;
      const charterChargeAmount = isCharterDeposit
        ? Math.round((charterDepositPercentage / 100) * finalAmount)
        : finalAmount;

      try {
        const chargeAmount = isCharterDeposit ? charterChargeAmount : finalAmount;
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/capture-payment-test`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              "Access-Control-Allow-Origin": process.env.REACT_APP_BASE_URL,
            },
            mode: "cors",
            body: JSON.stringify({
              tokenId: token.id,
              amount: chargeAmount,
            }),
          }
        );

        const result = await response.json();
        console.log("result: ", result);

        if (result.success) {
          // Update the status to "Paid" in your database
          await updateBookingStatus(storedBookingId, "Waiver Pending");

          const database = getDatabase();

          const paymentRef = ref(database, `payments/${storedPaymentId}`);

          let dbOwnerAmount = 0;
          if (totalOwnerBookings > 0) {
            dbOwnerAmount = amount;
          } else {
            dbOwnerAmount = amount - 10000; // 10000 = $100
          }

          try {
            let paymentData = {};
            paymentData = {
              totalAmount: finalAmount,
              paidAmount: isCharterDeposit ? charterChargeAmount : finalAmount,
              ownerAmount: isCharterDeposit ? charterChargeAmount - cutAdmin : amount - cutAdmin,
              discount: discount > 0 ? discount : 0,
              totalTax: taxTotal,
              adminCut: cutAdmin,
              adminTax: taxAdmin,
              dateTime: dayjs().format("dddd, MMMM D, YYYY HH:mm:ss"),
              transactionId: result.paymentIntent.id,
              userId: auth.currentUser.uid,
              bookingId: storedBookingId,
              owner: bookingData.owner,
            };

            // Charter deposit fields
            if (isCharterDeposit) {
              paymentData.charterDeposit = true;
              paymentData.charterDepositPercentage = charterDepositPercentage;
              paymentData.charterDepositPaid = charterChargeAmount;
              paymentData.charterCashBalance = finalAmount - charterChargeAmount;
              paymentData.cashBalanceDueToOwner = true;
            }

            if (totalOwnerBookings === 0) {
              paymentData.firstRentalPayment = 1; // Add the new field when totalOwnerBookings is 0
            }

            if (refferalAmount > 0) {
              paymentData.referrer = referId;
              paymentData.referAmount = refferalAmount;
              paymentData.adminFirstTimeCut = adminFirstTimeCut;
            }

            await update(paymentRef, paymentData);

            const renterData = await getCurrentUserDetails();
            const subjectEmail = "Payment Successful";
            const renterName = renterData.firstName + " " + renterData.lastName;
            const emailBody = await BookingStatusUpdateEmailBody(
              renterName,
              bookingData.listingTitle,
              "Paid"
            );
            sendEmail(renterData.email, subjectEmail, emailBody);

            const ownerData = await getUserByID(bookingData.owner);
            const subjectOwnerEmail = "Payment Successful";
            const ownerName = ownerData.firstName + " " + ownerData.lastName;
            const emailOwnerBody = await BookingStatusUpdateEmailBody(
              ownerName,
              bookingData.listingTitle,
              "Paid"
            );
            sendEmail(ownerData.email, subjectOwnerEmail, emailOwnerBody);

            const ownerMetaPayments = ref(
              database,
              `ownerMeta/${bookingData.owner}`
            );

            const previousOwnerPaymentsSnapshot = await get(ownerMetaPayments);

            if (previousOwnerPaymentsSnapshot.exists()) {
              const previousOwnerPayments = previousOwnerPaymentsSnapshot.val();

              const previousCurrentAmount =
                previousOwnerPayments.payments.currentAmount;
              const previousTotalAmount =
                previousOwnerPayments.payments.totalAmount;
              const ownerPayments = {
                currentAmount:
                  previousCurrentAmount +
                  (discount > 0
                    ? amount - cutAdmin - discount
                    : amount - cutAdmin),
                totalAmount:
                  previousTotalAmount +
                  (discount > 0
                    ? amount - cutAdmin - discount
                    : amount - cutAdmin),
              };
              if (previousOwnerPayments.previousTotalPaidBooking) {
                const updatedTotalPaidBookings =
                  parseInt(previousOwnerPayments.previousTotalPaidBookings) + 1;
                await update(ownerMetaPayments, {
                  payments: ownerPayments,
                  totalPaidBookings: updatedTotalPaidBookings,
                });
              } else {
                await update(ownerMetaPayments, {
                  payments: ownerPayments,
                  totalPaidBookings: 1,
                });
              }
            } else {
              const ownerPayments = {
                currentAmount:
                  discount > 0
                    ? amount - cutAdmin - discount
                    : amount - cutAdmin,
                totalAmount:
                  discount > 0
                    ? amount - cutAdmin - discount
                    : amount - cutAdmin,
              };
              await update(ownerMetaPayments, {
                payments: ownerPayments,
                totalPaidBookings: 1,
              });
            }
            // await holdPayment();
          } catch (error) {
            console.log("Error saving data: ", error.message);
            setLoading(false);
          }
          
          // Disable navigation blocking since payment is complete
          setShouldBlockNavigation(false);
          
          navigate("/dashboard/thank-you");
          setLoading(false);
        } else if (result.error) {
          console.error("Payment failed:", result.error);
          setError(result.error);
          setLoading(false);
        } else {
          console.error("Payment failed:");
          setError("Payment failed: ");
          setLoading(false);
        }
      } catch (error) {
        console.error("Error capturing payment:", error);
        setError(error);
        setLoading(false);
      }
    }
  };

  const holdPayment = async () => {
    if (!stripe || !elements) {
      return;
    }

    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement(CardElement),
    });

    if (error) {
      console.error(error);
    } else {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/payment-hold`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              amount: 80000,
              payment_method: paymentMethod.id,
            }),
          }
        );

        const result = await response.json();
        console.log("result: ", result);

        const database = getDatabase();
        const paymentRef = ref(database, `payments/${storedPaymentId}`);

        try {
          // Retrieve existing data
          const currentData = (await get(paymentRef)).val() || {};

          const paymentHeld = {
            id: result.paymentIntent.id,
            amount: result.paymentIntent.amount,
            status: "pending",
          };

          // Merge existing data with new data
          const mergedData = {
            ...currentData,
            paymentHeld: paymentHeld,
          };

          // Update the database with merged data
          await update(paymentRef, mergedData);
        } catch (error) {
          console.log("Error saving data: ", error.message);
        }
      } catch (error) {
        console.error("Error capturing payment:", error);
      }
    }
  };

  function formatDateTime(dateTimeString) {
    const options = {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };
    const formattedDateTimeNew = new Date(dateTimeString).toLocaleString(
      "en-US",
      options
    );
    return formattedDateTimeNew;
  }

  const offerPriceAsNumber = Number(offer.offerPrice);
  const offeredFormattedDateTime = formatDateTime(
    `${offer.offeredDate} ${offer.offeredTime}`
  );
  console.log(offer);

  return (
    <>
      {/* Navigation blocker modal - shown when trying to navigate via browser back/forward */}
      <Modal show={showNavigationModal} onHide={() => setShowNavigationModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Pending Payment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          You have a pending payment. You can complete this payment within 24 hours, otherwise the booking will be cancelled. Do you want to leave this page?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowNavigationModal(false);
            setPendingNavigation(null);
          }}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => {
            setShouldBlockNavigation(false);
            setShowNavigationModal(false);
            if (pendingNavigation) {
              setTimeout(() => navigate(pendingNavigation), 0);
            }
          }}>
            Continue
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="row">
        <div className="col">
          <div className="page-description d-flex justify-content-between">
            <h1>{bookingData.listingTitle}</h1>
          </div>
        </div>
      </div>

      <Row className="card p-4">
        <div class="mb-4">
          <div class="mt-2">
            <div class="row">
              <div class="col-sm-4">
                <p class="mb-0">Amount</p>
              </div>
              <div class="col-sm-8">
                <h3 class="text-muted mb-0 text-dark">
                  $
                  {discount > 0
                    ? parseFloat(offerPriceAsNumber) +
                      parseFloat(platformPercentages.salesTax) *
                        parseFloat(offerPriceAsNumber) -
                      parseFloat(discount / 100).toLocaleString()
                    : (
                        parseFloat(offerPriceAsNumber) +
                        parseFloat(platformPercentages.salesTax) *
                          parseFloat(offerPriceAsNumber)
                      ).toLocaleString()}
                </h3>
              </div>
            </div>
            <hr />
            <div class="row">
              <div class="col-sm-4">
                <p class="mb-0">Offered Date Time</p>
              </div>
              <div class="col-sm-8">
                <p class="text-muted mb-0">{offeredFormattedDateTime}</p>
              </div>
            </div>
            <hr />
            <div class="row">
              <div class="col-sm-4">
                <p class="mb-0">Duration</p>
              </div>
              <div class="col-sm-8">
                <p class="text-muted mb-0">{offer.duration}</p>
              </div>
            </div>
            <hr />
            <div class="row">
              <div class="col-sm-4">
                <p class="mb-0">Ariving Date Time</p>
              </div>
              <div class="col-sm-8">
                <p class="text-muted mb-0">{offer.arivingDateTime}</p>
              </div>
            </div>
          </div>
        </div>
        <Col>
          <form onSubmit={handleSubmit} className="mt-4">
            <div style={{ marginBottom: "20px" }}>
              {/* Style the CardElement */}
              <Row>
                <Col>
                  <TextField
                    id="standard-basic"
                    value={coupon}
                    onChange={(e) => setCouponCode(e.target.value)}
                    label="Coupon Code"
                    variant="standard"
                  />
                  <Button
                    variant="primary"
                    onClick={() => verifyDiscount(coupon)}
                    className="btn btn-primary m-1"
                  >
                    Apply
                  </Button>
                  {discount > 0 ? (
                    <Alert className="mt-3" severity="success">
                      -${discount / 100} discount has been applied.
                    </Alert>
                  ) : (
                    ""
                  )}
                </Col>
              </Row>
              <Row className="mt-3">
                <Col>
                  <CardElement
                    className="form-control"
                    options={{
                      style: {
                        base: {
                          fontSize: "16px",
                          color: "#424770",
                          "::placeholder": {
                            color: "#aab7c4",
                          },
                        },
                        invalid: {
                          color: "#9e2146",
                        },
                      },
                    }}
                  />
                  {error && (
                    <Alert className="mt-3" severity="error">
                      {error}
                    </Alert>
                  )}
                </Col>
              </Row>
              <Row className="mt-4">
                {/* <Col>
                <div className='d-flex mt-1 additional-box'>
                  <FormControlLabel style={{alignItems: 'flex-start'}} required control={<Checkbox checked={acknowledgement1} />} onChange={() => handleAcknowledgement('acknowledgement1')} />
                  <div>
                    <p style={{fontWeight: '500', lineHeight: '28px'}}>Please check this box to acknowledge that you will be responsible for a $800.00 refundable security fee <span style={{color: 'red'}}>*</span></p>
                    <p style={{fontStyle: 'italic', marginTop: '-11px'}}>Required for each rental transaction, returned within 7 days.</p>
                  </div>
                </div>

              </Col> */}
              </Row>
              <Row className="mt-4">
                <Col>
                  <div className="d-flex mt-1 additional-box">
                    <FormControlLabel
                      style={{ alignItems: "flex-start" }}
                      required
                      control={<Checkbox checked={acknowledgement2} />}
                      onChange={setAcknowledgement2}
                    />
                    <div>
                      <p style={{ fontWeight: "500", lineHeight: "28px" }}>
                        Please check this box to acknowledge that you have read
                        and agreed to the cancellation policy - refer to the{" "}
                        <Link to="/">website</Link> for additional cancellation
                        information <span style={{ color: "red" }}>*</span>
                      </p>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
            <button
              variant="primary"
              type="submit"
              className="btn btn-primary m-1"
            >
              {loading ? (
                <ThreeDots
                  visible={true}
                  height="22"
                  width="22"
                  color="#fff"
                  radius="20"
                  ariaLabel="three-dots-loading"
                  wrapperStyle={{}}
                  wrapperClass=""
                />
              ) : (
                "Pay"
              )}
            </button>
          </form>
        </Col>
      </Row>
    </>
  );
};

export default PaymentFormTest;
